import { useCallback, useEffect } from 'react';

import { useMapsLibrary, useMap } from '@vis.gl/react-google-maps';
import clsx from 'clsx';

import { placesFetchedFields } from '@/constants';
import { LocationOffset, useSearchStore } from '@/store/SearchStore';
import { getCurrentPosition } from '@/utils/geolocation';
import { getPlaceFromCoordinates } from '@/utils/maps';
import { getShapefileBounds, isLocationInBoundary } from '@/utils/polygon';

export const AutocompleteResults = () => {
  const { locationSearch, setLocationStart, setLocationEnd, searchIndex } = useSearchStore();
  const places = useMapsLibrary('places');
  const map = useMap();

  const listClasses =
    'w-full text-left flex-col border-b-border flex cursor-pointer border-b py-4 text-[#474C5E] underline-offset-4 hover:underline dark:border-b-gray-800 dark:text-white/75';

  const getLocationOffset = () => {
    const { locationSearch, locationStart, locationEnd, setSearching } = useSearchStore.getState();
    setSearching(!!locationStart && !!locationEnd);
    const offset = locationSearch?.offset;
    if (!offset) {
      throw Error('Missing locationSearch offset');
    }
    return offset;
  };

  const setPlace = (offset: LocationOffset, place: google.maps.places.Place) => {
    const setFn = offset === 'start' ? setLocationStart : setLocationEnd;
    setFn(place);
  };

  const handleUseMyLocation = async () => {
    const offset = getLocationOffset();
    const { coords } = (await getCurrentPosition()) ?? {};
    if (!coords) {
      return;
    }

    // Check if the current location is within bounds
    const isInBounds = isLocationInBoundary(coords.latitude, coords.longitude);
    if (!isInBounds) {
      const { setError, setLocationSearch } = useSearchStore.getState();
      setError(
        'Your current location is outside our air quality area. Enter a location inside the area shown on the map.',
      );
      setLocationSearch(undefined);
      return;
    }

    const place = await getPlaceFromCoordinates(coords.latitude, coords.longitude);
    if (!place) {
      throw Error('No place found for current location');
    }
    setPlace(offset, place);
  };

  const handleSuggestionClick = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      if (!places || !suggestion.placePrediction) return;

      const offset = getLocationOffset();
      const place = suggestion.placePrediction.toPlace();

      const { locationStart, locationEnd, setLocationSearch, setSearching } = useSearchStore.getState();
      const placeId = suggestion.placePrediction.placeId;

      if ((offset === 'start' && locationStart?.id === placeId) || (offset === 'end' && locationEnd?.id === placeId)) {
        setLocationSearch(undefined);
        setSearching(false);
        return;
      }

      await place.fetchFields({ fields: placesFetchedFields });

      setPlace(offset, place);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [places],
  );

  const { suggestions = [], filteredOutCount = 0, totalCount = 0 } = locationSearch || {};

  const showNoResultsMessage = suggestions.length === 0 && totalCount > 0 && filteredOutCount === 0;
  const showFilteredMessage = filteredOutCount > 0;

  useEffect(() => {
    if (showNoResultsMessage && map) {
      const shapefileBounds = getShapefileBounds();
      const bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(shapefileBounds[1], shapefileBounds[0]),
        new google.maps.LatLng(shapefileBounds[3], shapefileBounds[2]),
      );
      map.fitBounds(bounds);
    }
  }, [showNoResultsMessage, map]);

  if (!locationSearch) {
    return null;
  }

  const activeCss = 'outline-2 !outline-border !outline-offset-6';

  return (
    <div
      className="lt-lg:-mx-6 lt-sm:-mx-2 lt-lg:w-[calc(100%_+_3rem)] lt-sm:w-[calc(100%_+_1rem)] lt-lg:px-6 lt-sm:px-2 z-20 bg-white empty:hidden dark:bg-gray-900"
      id="autocomplete-results-listbox"
      role="listbox"
    >
      <button
        tabIndex={0}
        className={clsx(listClasses, 'flex-row items-center justify-between !text-[var(--blue-one)] underline', {
          [activeCss]: searchIndex === 0,
        })}
        onClick={handleUseMyLocation}
        onMouseDown={handleUseMyLocation}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleUseMyLocation();
          }
        }}
      >
        <span>Use my location</span>
        <LocationIcon />
      </button>
      {suggestions.map((s, index) => (
        <button
          key={index}
          id={`suggestion-item-${index + 1}`}
          tabIndex={0}
          className={clsx(listClasses, {
            [activeCss]: searchIndex === index + 1,
          })}
          onClick={() => handleSuggestionClick(s)}
          onMouseDown={() => handleSuggestionClick(s)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleSuggestionClick(s);
            }
          }}
        >
          {s.placePrediction?.mainText?.text}
          {s.placePrediction?.secondaryText?.text && (
            <span className="text-gray-500 dark:text-gray-400">{` ${s.placePrediction?.secondaryText?.text}`}</span>
          )}
        </button>
      ))}
      {showFilteredMessage && (
        <div className={clsx(listClasses, '!cursor-default')} style={{ pointerEvents: 'none' }}>
          <span className="text-gray-500 !no-underline dark:text-gray-400">
            {filteredOutCount} result{filteredOutCount > 1 ? 's' : ''} found {filteredOutCount > 1 ? 'are' : 'is'}{' '}
            outside our air quality area and {filteredOutCount > 1 ? 'have' : 'has'} been hidden
          </span>
        </div>
      )}
      {showNoResultsMessage && (
        <div className={clsx(listClasses, '!cursor-default')} style={{ pointerEvents: 'none' }}>
          <span className="text-gray-500 !no-underline dark:text-gray-400">
            No results found in our air quality area.
          </span>
        </div>
      )}
    </div>
  );
};

const LocationIcon = () => (
  <svg
    width="21"
    height="21"
    viewBox="0 0 21 21"
    fill="currentColor"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.2497 6.95716L2.75724 0.843726L2.74224 0.839039C2.47677 0.747635 2.19097 0.732573 1.91737 0.795565C1.64376 0.858557 1.39333 0.997075 1.19455 1.19536C0.995778 1.39365 0.856644 1.64374 0.792979 1.91719C0.729314 2.19064 0.743674 2.47647 0.834424 2.74216C0.835564 2.74729 0.837132 2.7523 0.839111 2.75716L6.95724 19.2497C7.05772 19.5437 7.24831 19.7986 7.50193 19.9782C7.75555 20.1578 8.05931 20.2529 8.37005 20.25H8.39817C8.71506 20.2476 9.02278 20.1434 9.27596 19.9528C9.52915 19.7623 9.71441 19.4954 9.80443 19.1915L9.81005 19.1728L11.8575 11.8603L19.1701 9.81279L19.1888 9.80716C19.4904 9.71436 19.755 9.52881 19.9451 9.27691C20.1351 9.02501 20.2409 8.71959 20.2473 8.40411C20.2538 8.08863 20.1606 7.77915 19.9809 7.51971C19.8013 7.26026 19.5445 7.06407 19.2469 6.95904L19.2497 6.95716ZM11.0475 10.5281C10.9237 10.5629 10.8109 10.6289 10.7199 10.7199C10.629 10.8108 10.5629 10.9236 10.5282 11.0475L8.37192 18.75L8.3663 18.734L2.25005 2.24998L18.7332 8.36435L18.7482 8.36998L11.0475 10.5281Z" />
  </svg>
);
